/**
 * Service bookings, from the institution's side.
 *
 * Two jobs: move a booking through its states, and write the result back.
 *
 * The result matters more than it looks. Whatever is saved here lands in the
 * patient's medical record and prints in their A4 file — so a scan done at this
 * hospital becomes part of a history that a different doctor will read later.
 * That is the difference between a booking system and a medical record.
 *
 * Completing a booking is also what records the revenue, which is why the
 * button says so rather than being a quiet status change.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  Select,
  Spinner,
  StatCard,
  Textarea,
} from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { serviceApi } from '../../api/endpoints';
import { formatCurrency } from '../../domain/pricing';
import { formatTime12, isoToDisplayDate, todayKey } from '../../domain/dates';
import { SERVICE_CATEGORY_LABELS } from '../../domain/institutions';
import { compressImage } from '../../domain/files';

const STATUS_LABEL = {
  pending: 'بانتظار التأكيد',
  confirmed: 'مؤكد',
  completed: 'مكتمل',
  cancelled: 'ملغي',
};

const STATUS_STYLE = {
  pending: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  confirmed: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300',
  completed: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  cancelled: 'border-red-500/30 bg-red-500/10 text-red-300',
};

export function InstitutionBookings() {
  const { success, error: toastError } = useNotifications();

  const [status, setStatus] = useState('');
  const [date, setDate] = useState('');

  const state = useAsync(
    () => serviceApi.bookings({ ...(status ? { status } : {}), ...(date ? { date } : {}) }),
    [status, date],
    { initialData: [] },
  );

  const rows = useMemo(() => state.data || [], [state.data]);
  const [resultTarget, setResultTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const totals = useMemo(
    () => ({
      all: rows.length,
      pending: rows.filter((b) => b.status === 'pending').length,
      today: rows.filter((b) => b.date === todayKey() && b.status !== 'cancelled').length,
      revenue: rows
        .filter((b) => b.status === 'completed')
        .reduce((sum, b) => sum + (Number(b.totalPrice) || 0), 0),
    }),
    [rows],
  );

  const setStatusFor = async (booking, next) => {
    setBusyId(booking.id);
    try {
      await serviceApi.setStatus(booking.id, next);
      success(
        next === 'completed'
          ? 'تم إنهاء الخدمة وتسجيل الإيراد'
          : `تم تحديث الحالة إلى ${STATUS_LABEL[next]}`,
      );
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر تحديث الحالة');
    } finally {
      setBusyId(null);
    }
  };

  const saveResult = async (payload) => {
    await serviceApi.saveResult(resultTarget.id, payload);
    success('تم حفظ النتيجة في السجل الطبي للمريض');
    setResultTarget(null);
    state.reload();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="حجوزات الخدمات"
        subtitle={`${totals.all} حجز`}
        icon="calendar-check"
        actions={
          <Button variant="ghost" icon="refresh-cw" onClick={state.reload} loading={state.loading}>
            تحديث
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon="calendar-check-2" label="إجمالي" value={totals.all} accent="cyan" />
        <StatCard icon="clock" label="بانتظار التأكيد" value={totals.pending} accent="amber" />
        <StatCard icon="sun" label="اليوم" value={totals.today} accent="purple" />
        <StatCard
          icon="banknote"
          label="إيراد المكتمل"
          value={formatCurrency(totals.revenue)}
          accent="emerald"
        />
      </div>

      <Panel>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">كل الحالات</option>
            {Object.entries(STATUS_LABEL).map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </Select>

          <Input type="date" dir="ltr" value={date} onChange={(event) => setDate(event.target.value)} />
        </div>

        {date && (
          <button
            type="button"
            onClick={() => setDate('')}
            className="mt-3 text-xs font-bold text-cyan-400 hover:text-cyan-300"
          >
            عرض كل التواريخ
          </button>
        )}
      </Panel>

      {state.error ? (
        <ErrorState error={state.error} onRetry={state.reload} />
      ) : state.loading && rows.length === 0 ? (
        <Spinner label="جاري تحميل الحجوزات..." />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon="calendar-x"
            title="لا توجد حجوزات"
            message="ستظهر هنا حجوزات المرضى لخدمات المنشأة."
          />
        </Panel>
      ) : (
        <div className="space-y-3">
          {rows.map((booking) => (
            <Panel key={booking.id} className="flex flex-wrap items-center gap-4">
              <span className="shrink-0 rounded-lg bg-slate-800 px-2.5 py-1.5 font-mono text-xs text-cyan-300">
                {booking.time === '00:00' ? 'يومي' : formatTime12(booking.time)}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="truncate font-bold text-white">{booking.serviceName}</h3>
                  <Badge className={STATUS_STYLE[booking.status] || ''}>
                    {STATUS_LABEL[booking.status] || booking.status}
                  </Badge>
                  {booking.isHomeVisit && (
                    <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300" icon="home">
                      منزلية
                    </Badge>
                  )}
                  {booking.insuranceApplied && (
                    <Badge className="border-indigo-500/30 bg-indigo-500/10 text-indigo-300" icon="shield-check">
                      تأمين
                    </Badge>
                  )}
                  {booking.depositRequired && !booking.depositPaid && (
                    <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300" icon="wallet">
                      عربون غير مدفوع
                    </Badge>
                  )}
                </div>

                <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-400">
                  <span>{booking.patient?.name || `مريض #${booking.patientId}`}</span>
                  <span>{SERVICE_CATEGORY_LABELS[booking.category] || booking.category}</span>
                  <span>{isoToDisplayDate(booking.date)}</span>
                  <span className="font-bold text-emerald-400">
                    {formatCurrency(booking.totalPrice)}
                  </span>
                </p>

                {booking.isHomeVisit && booking.visitAddress && (
                  <p className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
                    <Icon name="map-pin" size={11} />
                    {booking.visitAddress}
                  </p>
                )}
              </div>

              <div className="flex shrink-0 flex-wrap gap-2">
                <Button
                  variant="subtle"
                  icon="file-text"
                  onClick={() => setResultTarget(booking)}
                >
                  النتيجة
                </Button>

                {booking.status === 'pending' && (
                  <Button
                    variant="success"
                    icon="check"
                    loading={busyId === booking.id}
                    onClick={() => setStatusFor(booking, 'confirmed')}
                  >
                    تأكيد
                  </Button>
                )}

                {booking.status === 'confirmed' && (
                  <Button
                    variant="success"
                    icon="check-circle-2"
                    loading={busyId === booking.id}
                    onClick={() => setStatusFor(booking, 'completed')}
                  >
                    إنهاء
                  </Button>
                )}

                {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                  <Button
                    variant="danger"
                    icon="x"
                    loading={busyId === booking.id}
                    onClick={() => setStatusFor(booking, 'cancelled')}
                  >
                    إلغاء
                  </Button>
                )}
              </div>
            </Panel>
          ))}
        </div>
      )}

      <ResultModal
        booking={resultTarget}
        onClose={() => setResultTarget(null)}
        onSave={saveResult}
      />
    </div>
  );
}

/* ========================================================================== */

/**
 * The result, which is the part that becomes medical history.
 *
 * Attachments are compressed on the way in for the same reason receipts are:
 * they are stored as data URIs on the row, and an uncompressed phone photo of a
 * report is several megabytes of JSON on every subsequent read.
 */
function ResultModal({ booking, onClose, onSave }) {
  const { error: toastError } = useNotifications();

  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!booking) return;
    setNotes(booking.resultNotes || '');
    setFiles(booking.attachments || []);
  }, [booking]);

  if (!booking) return null;

  const addFiles = async (event) => {
    const picked = [...(event.target.files || [])];
    if (picked.length === 0) return;

    try {
      const added = await Promise.all(
        picked.map(async (file) => ({
          name: file.name,
          type: file.type,
          data: file.type.startsWith('image/')
            ? await compressImage(file, { maxWidth: 1600, quality: 0.75 })
            : await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
              }),
        })),
      );
      setFiles((current) => [...current, ...added]);
    } catch (err) {
      toastError(`تعذر قراءة الملف: ${err.message}`);
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      await onSave({ resultNotes: notes, attachments: files });
    } catch (err) {
      toastError(err.message || 'تعذر الحفظ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`نتيجة: ${booking.serviceName}`}
      icon="file-text"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button icon="save" loading={saving} onClick={submit}>
            حفظ في السجل الطبي
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <p className="rounded-lg border border-cyan-500/20 bg-cyan-500/[0.07] p-2.5 text-[11px] leading-relaxed text-cyan-100/90">
          ما يُحفظ هنا يظهر في السجل الطبي للمريض وفي ملفه المطبوع، ويمكن لأي طبيب يتابعه لاحقاً
          الاطلاع عليه.
        </p>

        <Field label="تقرير النتيجة">
          <Textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={6}
            placeholder="ملخص النتيجة، الملاحظات، التوصيات..."
          />
        </Field>

        <Field label="المرفقات" hint="صور التقارير أو ملفات PDF">
          <input
            type="file"
            multiple
            accept="image/*,application/pdf"
            onChange={addFiles}
            className="w-full text-xs text-slate-400 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-xs file:font-bold file:text-slate-200"
          />
        </Field>

        {files.length > 0 && (
          <ul className="space-y-2">
            {files.map((file, index) => (
              <li
                key={index}
                className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.04] p-2.5"
              >
                <Icon
                  name={String(file.type).startsWith('image/') ? 'image' : 'file-text'}
                  size={15}
                  className="shrink-0 text-cyan-400"
                />
                <span className="min-w-0 flex-1 truncate text-xs text-slate-300">{file.name}</span>
                <button
                  type="button"
                  onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
                  className="shrink-0 rounded-lg bg-red-500/10 p-1.5 text-red-300 hover:bg-red-500/20"
                  aria-label={`حذف ${file.name}`}
                >
                  <Icon name="trash-2" size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

export default InstitutionBookings;
